import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Upload, Camera, X, FileText, DollarSign } from 'lucide-react';
import { reimbursementAPI } from '@/lib/api';
import { CATEGORY_OPTIONS } from './constants';

// Agent submits a reimbursement request. At least one receipt (image or PDF,
// phone camera capture supported) is required before the form can be sent.
export default function ReimbursementFormDialog({ open, onOpenChange, onDone }) {
  const [category, setCategory] = useState('event');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState('');
  const [description, setDescription] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const reset = () => {
    setCategory('event'); setTitle(''); setAmount(''); setExpenseDate('');
    setDescription(''); setReceipts([]); setUploading(false); setSaving(false);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const r = await reimbursementAPI.uploadReceipt(f);
        setReceipts((prev) => [...prev, r.data]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (camRef.current) camRef.current.value = '';
  };

  const removeReceipt = (key) => setReceipts((prev) => prev.filter((r) => r.storage_key !== key));

  const submit = async () => {
    if (!title.trim()) { toast.error('Add a short title'); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Enter the amount to reimburse'); return; }
    if (receipts.length === 0) { toast.error('Attach at least one receipt'); return; }
    setSaving(true);
    try {
      await reimbursementAPI.create({
        category,
        title: title.trim(),
        description: description.trim() || null,
        amount: amt,
        expense_date: expenseDate || null,
        receipts,
      });
      toast.success('Reimbursement submitted');
      onOpenChange(false);
      reset();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="reimbursement-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign size={16} className="text-emerald-600" /> New Reimbursement</DialogTitle>
          <DialogDescription>Submit a cost for KR to reimburse. Attach at least one receipt — you can snap a photo from your phone.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="reimbursement-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`reimbursement-category-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Health fair table — Oct 12" data-testid="reimbursement-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount (USD)</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="reimbursement-amount" />
            </div>
            <div>
              <Label className="text-xs">Expense date</Label>
              <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} data-testid="reimbursement-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Details (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What was it for?" data-testid="reimbursement-description" />
          </div>

          <div>
            <Label className="text-xs">Receipts <span className="text-rose-500">*</span></Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="reimbursement-upload-btn">
                <Upload size={14} className="mr-1.5" /> Upload file
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => camRef.current?.click()} disabled={uploading} data-testid="reimbursement-camera-btn">
                <Camera size={14} className="mr-1.5" /> Take photo
              </Button>
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 self-center" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="reimbursement-file-input" />
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="reimbursement-camera-input" />
            {receipts.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="reimbursement-receipt-list">
                {receipts.map((r) => (
                  <li key={r.storage_key} className="flex items-center gap-2 text-sm rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid={`reimbursement-receipt-${r.storage_key}`}>
                    <FileText size={13} className="text-slate-400 shrink-0" />
                    <span className="truncate flex-1 text-slate-700">{r.filename}</span>
                    <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => removeReceipt(r.storage_key)} data-testid={`reimbursement-receipt-remove-${r.storage_key}`}><X size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-slate-400 mt-1">Images or PDF, up to 15 MB each.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button onClick={submit} disabled={saving || uploading} data-testid="reimbursement-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
